import * as CL from '@design-system/component-library';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import { shopMsg, t } from '../../common/i18n/index.js';
import { VaihtoetuCalculator } from './VaihtoetuCalculator.js';
import { VaihtoetuFaq } from './VaihtoetuFaq.js';
import type { CLStepperItem } from '../../common/types/componentLibrary.js';

import './Vaihtoetu.scss';

const VaihtoetuIntro = () => {
  const steps: CLStepperItem[] = [
    { id: '1', keepOpen: true, content: t.MGJQ('Check your estimated trade-in value.') },
    {
      id: '2',
      keepOpen: true,
      content: t.N9Q3(
        'Submit a contact request and we will get in touch with you with a trade-in offer and further instructions.'
      ),
    },
    {
      id: '3',
      keepOpen: true,
      content: t.ERCX('Send the device to us according to the instructions you receive.'),
    },
    {
      id: '4',
      keepOpen: true,
      content: t.R6WZ(
        'The credit will be applied to your billing agreement and can be used for future invoices and purchases.'
      ),
    },
  ];

  return (
    <div className={`${dsClass.PADDING_HORIZONTAL_3} of-vaihtoetu`}>
      <div className={`${dsClass.PADDING_HORIZONTAL_3} ${dsClass.PADDING_BOTTOM_7}`}>
        <div className={`${dsClass.DISPLAY_FLEX} ${dsClass.JUSTIFY_CONTENT_CENTER}`}>
          <h1 className={dsClass.H1}>{t.QEFQ('Elisa Vaihtoetu for Business Customers')} </h1>
        </div>
        <div className={`${dsClass.GRID_ROW} ${dsClass.JUSTIFY_CONTENT_CENTER}`}>
          <div className={`${dsClass.TEXT_LEAD} ${dsClass.TEXT_ALIGN_CENTER}`}>
            {t.S86I('Recycle your old devices and trade them in for credit for your business')}
          </div>
        </div>
      </div>
      <CL.StoryBlock
        ratio="1to1"
        invert={true}
        media={
          <CL.Picture
            className="of-vaihtoetu__story-block__image"
            src="https://static.elisa.com/v2/image/2tqybbhjs47b/3WwCbw73N63Ck5v7AnRLUW/Elisa-ya-vaihtoetu.png"
            i18n_picture_altText={t.QEFQ('Elisa Vaihtoetu for Business Customers')}
          />
        }
      >
        <div className={dsClass.RESET}>
          <CL.Stepper id="vaihtoetuStepper" className={dsClass.TEXT_M} steps={steps} />
        </div>
      </CL.StoryBlock>
      <div>
        <hr className={dsClass.MARGIN_VERTICAL_7} />
        <h2 className={dsClass.H2}>{t.L9TJ('Why use the trade-in benefit?')}</h2>
        <div>
          <ul>
            <li>{t.U85D('You receive a credit based on the device model and condition')}</li>
            <li>
              {t.KMOP(
                'As an Elisa business customer, you can allocate the credit, for example, to monthly fees for subscriptions and services'
              )}
            </li>
            <li>{t.EXDT('Your old device is always recycled reliably and securely ')}</li>
          </ul>
        </div>
        <h3 className={dsClass.H3_BOOK}>{t.HE3T('Want to return multiple devices at once?')}</h3>
        <div className={dsClass.MARGIN_BOTTOM_7}>
          <ul>
            <li>{t.USAF('Create a separate price estimate for each device')}</li>
            <li>
              {t.ZB5M('We will combine the credits and transfer the total amount to your company’s billing agreement')}
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

const CheckOnlineStore = () => {
  return (
    <div className={`${dsClass.PADDING_HORIZONTAL_3} of-vaihtoetu`}>
      <CL.StoryBlock
        ratio="1to1"
        className={dsClass.MARGIN_VERTICAL_7}
        media={
          <CL.Picture
            src="https://static.elisa.com/v2/image/2tqybbhjs47b/6mH5tcIqSQndMzzzO4PYSx/Hero.jpg?fm=webp&q=80&w=1920"
            i18n_picture_altText={t.BZYZ(shopMsg)}
          />
        }
      >
        <h2 className={dsClass.H2}>{t.FYWX('Order phones and other devices easily online ')}</h2>
        <p className={dsClass.PADDING_BOTTOM_5}>
          {t.S5JH(
            'In Elisa’s online store for business customers, you’ll find everything you need for smooth everyday work and office use. Order devices easily online.'
          )}
        </p>
        <CL.Link
          className="of-vaihtoetu__form-button"
          linkStyle="link-button"
          buttonSize="l"
          linkTarget="_blank"
          buttonColor="light"
          linkHref={paths.WEB_SHOP}
        >
          {t.Z5E1('Explore the selection')}
        </CL.Link>
      </CL.StoryBlock>
      <hr className={dsClass.MARGIN_VERTICAL_7} />
    </div>
  );
};

export const VaihtoetuLanding = () => {
  return (
    <>
      <CL.Hero
        bgImage={
          <CL.Picture
            sources={[
              {
                srcSet: 'https://static.elisa.com/v2/image/2tqybbhjs47b/5BuKbVY2hpC1t5Ze4nYxOH/Vaihtoetu-720x1080.jpg',
                media: '(max-width: 640px)',
              },
              {
                srcSet:
                  'https://static.elisa.com/v2/image/2tqybbhjs47b/1B5fRMnzZNl90jZtIUq3bL/Elisa-Vaihtoetu_2560x1014_nologo.jpg',
                media: '(min-width: 641px)',
              },
            ]}
            fit="cover"
            position="center center"
          />
        }
        bgColor="blue-800"
        gradientColor="blue-800"
        mobileContentPosition="below"
      >
        <CL.Picture
          src="https://static.elisa.com/v2/image/2tqybbhjs47b/5cA0dKBsJRL9eGXgqsyu2G/Elisa_Vaihtoetu_Badge_OLD_COLORWAY-01.png"
          fit="cover"
          className="of-vaihtoetu__logo"
          position="center center"
        />
        <h1 className={dsClass.H1}>{t.N7WM('Always a fair refund for your old device')}</h1>
      </CL.Hero>
      <VaihtoetuIntro />
      <VaihtoetuCalculator />
      <CheckOnlineStore />
      <VaihtoetuFaq />
    </>
  );
};
